import { Component, EventEmitter, Inject, Injector, Input, OnDestroy, OnInit, Output, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';

import { BehaviorSubject, Subscription } from 'rxjs';

import { ActionService, AuthService, cleanObject, CustomFormatter, ISitemap, ISitemapCommand, SITEMAP } from 'pbc.angular';

import { IPostVermerkRequest, IPostVermerkResponse } from 'fa-kt.types';
import { PostVermerkCommandService } from '../service';

@Component({
  selector: 'fa-kt-post-vermerk',
  templateUrl: './post-vermerk.component.html',
  styleUrls: ['./post-vermerk.component.css'],
})
export class PostVermerkCommandComponent implements OnInit, OnDestroy {
  command!: ISitemapCommand;

  subscriptions: Subscription[] = [];
  form!: FormGroup;
  $loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $valid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  CustomFormatter = CustomFormatter;

  @Input() set loading(loading: boolean) {
    this.$loading.next(this.$loading.getValue() || loading);
  }
  @Input() set value(value: IPostVermerkRequest) {
    this.patch(value);
  }
  @Output() valueChanged: EventEmitter<IPostVermerkRequest> = new EventEmitter<IPostVermerkRequest>();
  @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() finished: EventEmitter<IPostVermerkResponse> = new EventEmitter<IPostVermerkResponse>();

  /* << fields    */
  /*    fields >> */

  get modalRef() {
    return this.injector.get(NzModalRef);
  }

  constructor(
    @Inject(SITEMAP) private sitemap: ISitemap,
    private fb: FormBuilder,
    private injector: Injector,
    private viewContainerRef: ViewContainerRef,
    private modal: NzModalService,
    private message: NzMessageService,
    private actions: ActionService,
    public auth: AuthService,
    public postVermerk: PostVermerkCommandService /* << more    */ /*    more >> */,
  ) {
    this.command = sitemap.KOMMUNIKATION.Commands.POST_VERMERK;
    this.form = this.fb.group({
      vermerk: this.fb.group({
        id: [null, []],
        projekt: [null, []],
        kunde: [null, []],
        antwortAuf: [null, []],
        verfasser: [null, [Validators.required]],
        kommentar: [null, [Validators.required]],
        externSichtbar: [null, []],
        order: [null, [Validators.required]],
      }),
    });
    /* << constructor    */
    /*    constructor >> */
    this.patch();
  }

  ngOnInit() {
    this.postVermerk.prepare().catch((e) => console.error(e));
    this.subscriptions.push(
      this.finished.subscribe((result) => {
        if (this.modalRef) {
          this.modalRef.destroy(result);
        }
      }),
      this.valid.subscribe((valid) => {
        if (valid) {
          this.actions.subscribe({ key: this.command.translation, action: async () => await this.submit() });
        } else {
          this.actions.unsubscribe(this.command.translation);
        }
      }),
      this.form.valueChanges.subscribe((value: IPostVermerkRequest) => {
        this.valueChanged.emit(value);
        this.valid.emit(this.form.valid);
      }),
    );
    /* << init    */
    /*    init >> */
  }

  ngOnDestroy() {
    /* << end    */
    /*    end >> */
    this.actions.unsubscribe(this.command.translation);
    this.subscriptions.filter(($: Subscription) => $ && !$.closed).forEach(($: Subscription) => $.unsubscribe());
  }

  private patch(value?: IPostVermerkRequest) {
    if (value) {
      value = cleanObject(value);
      if (value.vermerk) {
        value.vermerk = cleanObject(value.vermerk);
      }
      this.form.patchValue(value);
    }
    this.update();
  }

  update() {
    this.$loading.next(true);
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.$valid.next(this.form.valid);
    this.$loading.next(false);
  }

  async submit() {
    this.update();
    if (!this.form.valid) {
      return;
    }
    this.$loading.next(true);
    try {
      const payload = cleanObject(this.form.getRawValue());
      if (payload.vermerk) {
        payload.vermerk = cleanObject(payload.vermerk);
      }
      const response: IPostVermerkResponse = await this.postVermerk.request(payload);
      this.vermerkKommentar?.patchValue('');
      this.finished.next(response);
    } catch (error: any) {
      this.message.error(error.message);
      for (const validation of error.errors ? error.errors : []) {
        for (const children of validation.children ? validation.children : []) {
          const message = Object.values(children.constraints).reduce((acc, val) => acc + ' ' + val, '');
          this.form.controls[validation.property].get(children.property)?.setErrors({ server: message });
        }
      }
    }
    this.$loading.next(false);
  }

  get vermerkId() {
    return this.form.controls.vermerk.get('id');
  }
  get vermerkProjekt() {
    return this.form.controls.vermerk.get('projekt');
  }
  get vermerkKunde() {
    return this.form.controls.vermerk.get('kunde');
  }
  get vermerkAntwortAuf() {
    return this.form.controls.vermerk.get('antwortAuf');
  }
  get vermerkVerfasser() {
    return this.form.controls.vermerk.get('verfasser');
  }
  get vermerkKommentar() {
    return this.form.controls.vermerk.get('kommentar');
  }
  get vermerkExternSichtbar() {
    return this.form.controls.vermerk.get('externSichtbar');
  }
}
